/**
 * StreamBuffer - Buffered streaming utility for smoother text delivery
 * Batches small chunks together and flushes at regular intervals
 */

import {
	STREAM_FLUSH_INTERVAL_MS,
	STREAM_IMMEDIATE_FLUSH_THRESHOLD,
	STREAM_DELAY_MS,
	PROGRESS_HEARTBEAT_INTERVAL_MS
} from './constants';

export type StreamCallback = (chunk: string) => void;

/**
 * StreamBuffer class for managing buffered streaming output
 * Provides smooth text delivery by batching small chunks
 */
export class StreamBuffer {
	private buffer: string = '';
	private flushTimer: ReturnType<typeof setTimeout> | null = null;
	private callback: StreamCallback | undefined;
	private fullText: string = '';

	constructor(callback?: StreamCallback) {
		this.callback = callback;
	}

	/**
	 * Check if the buffer has a callback configured
	 */
	hasCallback(): boolean {
		return !!this.callback;
	}

	/**
	 * Get all text that has been written (including already flushed)
	 */
	getFullText(): string {
		return this.fullText;
	}

	/**
	 * Write a chunk to the buffer
	 * Large chunks or chunks with newlines are flushed immediately
	 * Small chunks are batched and flushed after a short interval
	 *
	 * @param chunk - Text chunk to write
	 */
	write(chunk: string): void {
		this.fullText += chunk;

		if (!this.callback) {
			return;
		}

		this.buffer += chunk;

		// Flush immediately for large chunks or chunks with newlines
		if (chunk.includes('\n') || chunk.length > STREAM_IMMEDIATE_FLUSH_THRESHOLD) {
			this.flushNow();
		} else if (!this.flushTimer) {
			// Schedule flush for small chunks
			this.flushTimer = setTimeout(() => this.flushNow(), STREAM_FLUSH_INTERVAL_MS);
		}
	}

	/**
	 * Flush the buffer immediately
	 * Call this before tool execution or other operations that need clean output
	 */
	flush(): void {
		this.flushNow();
	}

	/**
	 * Internal flush implementation
	 */
	private flushNow(): void {
		if (this.flushTimer) {
			clearTimeout(this.flushTimer);
			this.flushTimer = null;
		}

		if (this.buffer && this.callback) {
			this.callback(this.buffer);
			this.buffer = '';
		}
	}

	/**
	 * Clean up resources
	 * Call this when done with the buffer
	 */
	dispose(): void {
		this.flush();
		this.callback = undefined;
	}
}

/**
 * TaggedStreamBuffer - Wraps a parent StreamBuffer and automatically tags every write
 * with a unique call ID. Used by source agents so the frontend can group
 * interleaved messages from parallel source agents into separate blocks.
 *
 * Format: __SB_{callId}_{type}__{content}__SB_END__
 * - type: "START" for the first message (includes source name + intent)
 * - "MSG" for subsequent messages
 *
 * The frontend parses these markers to group content by callId and render
 * each group as a collapsible section.
 */
export class TaggedStreamBuffer extends StreamBuffer {
	private callId: string;
	private parent: StreamBuffer;
	private sourceName: string;
	private intent: string;
	private started: boolean = false;

	constructor(callId: string, parent: StreamBuffer, sourceName: string, intent: string) {
		// Pass no callback — we don't use the base class buffering
		super();
		this.callId = callId;
		this.parent = parent;
		this.sourceName = sourceName;
		this.intent = intent;
	}

	hasCallback(): boolean {
		return this.parent.hasCallback();
	}

	getFullText(): string {
		return this.parent.getFullText();
	}

	write(chunk: string): void {
		if (!this.parent.hasCallback()) return;

		if (!this.started) {
			// First write: emit START marker with source name and intent
			this.started = true;
			const escapedName = this.sourceName.replace(/\|/g, '-');
			const escapedIntent = this.intent
				.replace(/\|/g, ' ')       // Pipe used as separator — must be sanitized
				.replace(/"/g, '&quot;');
			this.parent.write(`__SB_${this.callId}_START__${escapedName}|${escapedIntent}__SB_END__`);
		}

		// Tag the content
		this.parent.write(`__SB_${this.callId}_MSG__${chunk}__SB_END__`);
	}

	flush(): void {
		this.parent.flush();
	}

	dispose(): void {
		this.parent.flush();
	}
}

/**
 * Helper function for small delays to improve streaming UX
 * @param ms - Delay in milliseconds (default from constants)
 */
export function streamDelay(ms: number = STREAM_DELAY_MS): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Execute an async operation with progress heartbeat
 * Sends periodic updates to keep the user informed during long-running operations
 *
 * @param operation - The async operation to execute
 * @param progressMessage - Message to show during execution
 * @param streamBuffer - StreamBuffer instance for output
 * @param intervalMs - Heartbeat interval (default from constants)
 * @returns The result of the operation
 */
export async function withProgressHeartbeat<T>(
	operation: () => Promise<T>,
	progressMessage: string,
	streamBuffer: StreamBuffer,
	intervalMs: number = PROGRESS_HEARTBEAT_INTERVAL_MS
): Promise<T> {
	if (!streamBuffer.hasCallback()) {
		// No streaming callback, just execute the operation
		return operation();
	}

	const startTime = Date.now();

	// Send initial progress indicator
	await streamDelay(30);
	streamBuffer.write(`⏳ ${progressMessage}`);

	try {
		const result = await operation();
		return result;
	} finally {
		// Show final elapsed time
		const totalSeconds = Math.floor((Date.now() - startTime) / 1000);
		if (totalSeconds >= 1) {
			streamBuffer.write(` (${totalSeconds}s)`);
		}
		streamBuffer.write('\n\n');
	}
}
